import React from 'react'

import { getClient } from '@mondough/analytics-v2'

export interface TrackFeatureViewOptions {
  /** The name of the feature being tracked */
  name: string
  /** Custom parameters to include with the view event */
  customParameters?: Record<string, unknown>
  /** Tags for categorization and filtering */
  tags?: string[]
  /** Whether the feature should be tracked (useful for conditional tracking) */
  shouldTrack?: boolean
  /** Dependencies that should trigger re-tracking when they change */
  dependencies?: React.DependencyList
  /** Placement ID provided by the backend for recommendation tracking */
  placement_id?: string
  /** Whether to wait for the component to be visible before tracking (default: true) */
  waitForVisibility?: boolean
  /** Threshold for intersection observer (0-1, default: 0.1) */
  visibilityThreshold?: number
}

/**
 * Hook for tracking feature views using analyticsClient.trackView.
 *
 * This hook provides a standardized way to track when a feature component
 * becomes visible to the user, including features below the fold.
 *
 *
 **/
export const useTrackFeatureView = ({
  name,
  customParameters = {},
  tags = [],
  shouldTrack = true,
  dependencies = [],
  placement_id,
  waitForVisibility = true,
  visibilityThreshold = 0.1,
}: TrackFeatureViewOptions) => {
  const elementRef = React.useRef<HTMLDivElement>(null)
  const hasTracked = React.useRef(false)

  const trackView = React.useCallback(() => {
    if (hasTracked.current) return

    const analyticsClient = getClient()
    if (!analyticsClient) return

    const finalCustomParameters = {
      ...customParameters,
      ...(placement_id && { placement_id }),
    }

    analyticsClient.trackView({
      name,
      customParameters: finalCustomParameters,
      tags,
    })

    hasTracked.current = true
  }, [name, customParameters, tags, placement_id])

  React.useEffect(() => {
    hasTracked.current = false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies)

  React.useEffect(() => {
    if (!shouldTrack) return

    if (!waitForVisibility) {
      // Track immediately if visibility detection is disabled
      trackView()
      return
    }

    const element = elementRef.current
    if (!element) return

    const supportsIntersectionObserver =
      'IntersectionObserver' in window &&
      'IntersectionObserverEntry' in window &&
      'intersectionRatio' in window.IntersectionObserverEntry.prototype

    if (!supportsIntersectionObserver) {
      // track that we attempted to use intersection observer but it wasn't supported
      const analyticsClient = getClient()
      if (analyticsClient) {
        analyticsClient.trackData({
          name: `track-feature-view.intersection-observer-not-supported.${name}`,
        })
      }
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (
          entry &&
          entry.isIntersecting &&
          entry.intersectionRatio >= visibilityThreshold
        ) {
          trackView()
          observer.disconnect() // Only track once
        }
      },
      {
        threshold: visibilityThreshold,
        rootMargin: '0px',
      },
    )

    observer.observe(element)

    return () => {
      observer.disconnect()
    }
  }, [
    name,
    shouldTrack,
    waitForVisibility,
    visibilityThreshold,
    trackView,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ...dependencies,
  ])

  return elementRef
}
